import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import FormatListNumberedRounded from "@mui/icons-material/FormatListNumberedRounded";
import TableChartRounded from "@mui/icons-material/TableChartRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import type { CourtCreate } from "@/libs/facilityApi";
import { formatCourtTagLabel } from "@bw/shared/utils";

import { normalizeCourtTags } from "./bulk-court-draft";

export type BulkCourtAuthoringMode = "range" | "csv";

interface BulkCourtAuthoringProps {
  mode: BulkCourtAuthoringMode;
  busy: boolean;
  prefix: string;
  startNumber: string;
  count: string;
  zeroPadding: string;
  capacity: string;
  defaultTags: string;
  csv: string;
  draft: CourtCreate[] | null;
  onModeChange: (mode: BulkCourtAuthoringMode) => void;
  onPrefixChange: (value: string) => void;
  onStartNumberChange: (value: string) => void;
  onCountChange: (value: string) => void;
  onZeroPaddingChange: (value: string) => void;
  onCapacityChange: (value: string) => void;
  onDefaultTagsChange: (value: string) => void;
  onCsvChange: (value: string) => void;
  onPrepare: () => void;
  onUpdateDraft: (index: number, update: Partial<CourtCreate>) => void;
  onRemoveDraftRow: (index: number) => void;
}

function courtCountLabel(count: number): string {
  return `${count} ${count === 1 ? "court" : "courts"}`;
}

export function BulkCourtAuthoring({
  mode,
  busy,
  prefix,
  startNumber,
  count,
  zeroPadding,
  capacity,
  defaultTags,
  csv,
  draft,
  onModeChange,
  onPrefixChange,
  onStartNumberChange,
  onCountChange,
  onZeroPaddingChange,
  onCapacityChange,
  onDefaultTagsChange,
  onCsvChange,
  onPrepare,
  onUpdateDraft,
  onRemoveDraftRow,
}: BulkCourtAuthoringProps) {
  return (
    <Stack spacing={2}>
      <Tabs
        aria-label="Bulk court input method"
        onChange={(_event, value: BulkCourtAuthoringMode) =>
          onModeChange(value)
        }
        value={mode}
        variant="fullWidth"
      >
        <Tab
          aria-controls="bulk-court-range-panel"
          disabled={busy}
          icon={<FormatListNumberedRounded fontSize="small" />}
          iconPosition="start"
          id="bulk-court-range-tab"
          label="Numbered range"
          value="range"
        />
        <Tab
          aria-controls="bulk-court-csv-panel"
          disabled={busy}
          icon={<TableChartRounded fontSize="small" />}
          iconPosition="start"
          id="bulk-court-csv-tab"
          label="CSV import"
          value="csv"
        />
      </Tabs>

      {mode === "range" ? (
        <Box
          aria-labelledby="bulk-court-range-tab"
          id="bulk-court-range-panel"
          role="tabpanel"
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          <TextField
            disabled={busy}
            fullWidth
            id="bulk-court-prefix"
            label="Prefix"
            onChange={(event) => onPrefixChange(event.target.value)}
            size="small"
            value={prefix}
          />
          <TextField
            disabled={busy}
            fullWidth
            id="bulk-court-start"
            label="Starting number"
            onChange={(event) => onStartNumberChange(event.target.value)}
            size="small"
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            value={startNumber}
          />
          <TextField
            disabled={busy}
            fullWidth
            id="bulk-court-count"
            label="Count"
            onChange={(event) => onCountChange(event.target.value)}
            size="small"
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            value={count}
          />
          <TextField
            disabled={busy}
            fullWidth
            id="bulk-court-padding"
            label="Zero-padding"
            onChange={(event) => onZeroPaddingChange(event.target.value)}
            size="small"
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            value={zeroPadding}
          />
          <TextField
            disabled={busy}
            fullWidth
            id="bulk-court-capacity"
            label="Capacity (optional)"
            onChange={(event) => onCapacityChange(event.target.value)}
            size="small"
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
            value={capacity}
          />
          <TextField
            disabled={busy}
            fullWidth
            id="bulk-court-tags"
            label="Default tags"
            onChange={(event) => onDefaultTagsChange(event.target.value)}
            placeholder="bookable, premium"
            size="small"
            value={defaultTags}
          />
        </Box>
      ) : (
        <Box
          aria-labelledby="bulk-court-csv-tab"
          id="bulk-court-csv-panel"
          role="tabpanel"
        >
          <TextField
            disabled={busy}
            fullWidth
            helperText="Header: name,capacity,tags. Separate tags with |. Capacity may be blank."
            id="bulk-court-csv"
            label="CSV court data"
            minRows={7}
            multiline
            onChange={(event) => onCsvChange(event.target.value)}
            placeholder={
              'name,capacity,tags\n"Center Court",4,premium|bookable'
            }
            value={csv}
            sx={{
              "& .MuiInputBase-inputMultiline": {
                fontFamily: "monospace",
                fontSize: "0.8125rem",
              },
            }}
          />
        </Box>
      )}

      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button
          disabled={busy}
          onClick={onPrepare}
          size="small"
          type="button"
          variant="outlined"
        >
          Prepare editable draft
        </Button>
      </Stack>

      {draft !== null ? (
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "baseline", justifyContent: "space-between" }}
          >
            <Typography component="h3" variant="subtitle2">
              Draft · {courtCountLabel(draft.length)}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Edit before server review
            </Typography>
          </Stack>
          <Paper
            variant="outlined"
            sx={{
              maxHeight: "min(42dvh, 24rem)",
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
          >
            {draft.map((court, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: "minmax(0, 1fr) 6rem auto",
                  borderBottom: index < draft.length - 1 ? 1 : 0,
                  borderColor: "divider",
                  p: 1,
                }}
              >
                <TextField
                  disabled={busy}
                  onChange={(event) =>
                    onUpdateDraft(index, { name: event.target.value })
                  }
                  size="small"
                  slotProps={{
                    htmlInput: { "aria-label": `Row ${index + 1} name` },
                  }}
                  value={court.name}
                />
                <TextField
                  disabled={busy}
                  onChange={(event) =>
                    onUpdateDraft(index, {
                      max_players:
                        event.target.value === ""
                          ? undefined
                          : Number(event.target.value),
                    })
                  }
                  size="small"
                  slotProps={{
                    htmlInput: {
                      "aria-label": `Row ${index + 1} capacity`,
                      inputMode: "numeric",
                    },
                  }}
                  value={court.max_players ?? ""}
                />
                <Tooltip title="Remove row">
                  <span>
                    <IconButton
                      aria-label={`Remove ${court.name}`}
                      disabled={busy}
                      onClick={() => onRemoveDraftRow(index)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <TextField
                  disabled={busy}
                  onChange={(event) =>
                    onUpdateDraft(index, {
                      tags: normalizeCourtTags(
                        event.target.value.split(/[,|]/),
                      ),
                    })
                  }
                  size="small"
                  slotProps={{
                    htmlInput: { "aria-label": `Row ${index + 1} tags` },
                  }}
                  value={(court.tags ?? []).join(", ")}
                  sx={{ gridColumn: "1 / -1" }}
                />
              </Box>
            ))}
          </Paper>
        </Stack>
      ) : null}
    </Stack>
  );
}

export function BulkCourtReview({ courts }: { courts: CourtCreate[] }) {
  return (
    <TableContainer
      component={Paper}
      data-testid="bulk-court-review-list"
      variant="outlined"
      sx={{
        maxHeight: "min(58dvh, 32rem)",
        overflowY: "auto",
        overscrollBehavior: "contain",
      }}
    >
      <Table aria-label="Courts ready to create" size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Court</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell align="right">Status / capacity</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {courts.map((court, index) => (
            <TableRow
              data-testid="bulk-court-review-row"
              key={`${court.name}-${index}`}
            >
              <TableCell sx={{ fontWeight: 600 }}>{court.name}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {(court.tags ?? []).map(formatCourtTagLabel).join(", ") ||
                  "No tags"}
              </TableCell>
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                {court.status} ·{" "}
                {court.max_players == null
                  ? "No court-specific limit"
                  : `${court.max_players} players`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
